import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';
import Offcanvas, { OffcanvasPlacement } from 'react-bootstrap/Offcanvas';
import { SignalingOpportunityDTO, VehicleEntity } from 'src/api';
import { OpportunityAttachment } from 'src/api/models/AccountEntity';
import { useApiClient } from 'src/hooks/useApiClient';
import { downloadBase64File, formatDate, renderFloatAsStr } from 'src/utils';
import { OPPORTUNITY_STATES_DATA } from 'src/utils/constants';
import { translateOpportunityState } from 'src/utils/translations';
import VehicleCardBody from '../VehicleCardBody/VehicleCardBody';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { OpportunityBackofficeDTO } from 'src/api/models/Opportunities/OpportunityBackofficeDTO';
import { FaDownload } from 'react-icons/fa';
import { useAuth } from 'src/hooks/useAuth';

type Props = {
  placement?: OffcanvasPlacement | undefined;
  opportunity?: SignalingOpportunityDTO | null;
  onOpenChange: (bool: boolean) => void;
  open?: boolean
}

type KeyValueItem = {
  label: React.ReactNode;
  value: React.ReactNode;
};

export const KeyValueTable: React.FC<{ items: KeyValueItem[]; title?: string; className?: string }> = ({ items, title, className }) => {
  return (
    <div className={className}>
      {title && <h5 className="mb-3">{title}</h5>}

      <Table className="w-full" size="sm" responsive>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td className="fw-medium py-2">
                {item.label}
              </td>
              <td className="text-end fw-semibold py-2">
                {item.value}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export function ReportDetailOverlay({ open = false, onOpenChange, opportunity, placement = 'end' }: Props) {
  const { user } = useAuth();
  const { apiClient, handleApi } = useApiClient();
  const [vehicleResponse, setVehicleResponse] = useState<VehicleEntity | null>(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState<string | null>(null);
  const [documents, setDocuments] = useState([]);
  const [, setBackofficeResponse] = useState<OpportunityBackofficeDTO | null>(null);
  const { showError } = useErrorToast();

  useEffect(() => {
    !!opportunity?.Id && loadBackofficeData(opportunity.Id);
    opportunity?.ID_Vehicle_HMP__c && loadVehicleData(opportunity?.ID_Vehicle_HMP__c)
    return () => {
      setVehicleResponse(null);
      setDocuments([]);
    }
  }, [opportunity])


  const loadVehicleData = async (vehicleId: string) => {
    try {
      const vehicleRes = await handleApi(
        apiClient.vehicleController.getVehicles1(vehicleId)
      );
      setVehicleResponse(vehicleRes);
      return vehicleRes;
    } catch (error) {
      showError(error, i18next.t('failed_to_load_vehicle_data'))
    }
  };

  const loadBackofficeData = async (opportunityId: string) => {
    try {
      const backofficeData = await handleApi(
        apiClient.salesforceOpportunityController.getOpportunityBackoffice(opportunityId)
      );
      setBackofficeResponse(backofficeData)
      setDocuments(backofficeData?.loadedDocuments || [])
    } catch (error) {
      showError(error, i18next.t('failed_to_load_backoffice_data'))
    }
  }

  if (!opportunity) return null;

  const opportunityData: KeyValueItem[] = [
    { label: i18next.t('name'), value: opportunity?.Name },
    { label: i18next.t('stato'), value: translateOpportunityState(OPPORTUNITY_STATES_DATA[opportunity.StageName]?.label) },
    { label: i18next.t('created_at'), value: opportunity?.CreatedDate ? formatDate(opportunity?.CreatedDate, 'it-IT', false) : '' },
    { label: i18next.t('commissions'), value: renderFloatAsStr(opportunity?.Provvigioni_Segnalatore__c ? opportunity?.Provvigioni_Segnalatore__c?.toFixed(2) : '0.00') },
    { label: i18next.t('fiscal_code'), value: opportunity.codiceFiscale },
    { label: i18next.t('business_fiscal_code'), value: opportunity.partitaIva },
    { label: i18next.t('phone'), value: opportunity.phone },
    { label: i18next.t('email'), value: opportunity.email },
    { label: i18next.t('rental_company'), value: opportunity.Noleggiatore__c },
    { label: i18next.t('notes'), value: opportunity.Messaggio__c },
  ];

  const vehicleData: KeyValueItem[] = [
    { label: i18next.t('brand'), value: opportunity.Brand__c || opportunity.Marca__c || i18next.t('not_available') },
    { label: i18next.t('model'), value: opportunity.Modello__c || i18next.t('not_available') },
  ];

  const downloadAttachment = async (a: OpportunityAttachment, key: string) => {
    setDownloadingAttachment(key);
    try {
      const presignedReponse = await handleApi(
        apiClient.salesforceOpportunityController.getOpportunityAttachment(a.Id)
      );

      if (presignedReponse && presignedReponse.downloadUrl) {
        const link = document.createElement('a');
        link.href = presignedReponse.downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      showError(error, i18next.t('failed_to_download_file'));
    } finally {
      setDownloadingAttachment(null);
    }
  };

  const downloadLoadedDocument = async ({ fileName, key }) => {
    const attachment = opportunity?.attachments?.find(
      (item) => item.Title === fileName
    );
    attachment && downloadAttachment(attachment, key);
  };


  const documentPredicate = (doc: any) => doc?.createdBy
    && user?.username
    && doc.createdBy?.toLowerCase() == user?.username;

  const documentData: KeyValueItem[] = (documents || []).filter(documentPredicate).map((item: any) => ({
    label: item?.fileName, value: (
      <Button
        disabled={downloadingAttachment === item.key}
        variant="link"
        onClick={() => downloadLoadedDocument(item)}
      >
        {downloadingAttachment === item.key
          ?
          <Spinner animation="border" size="sm" />
          :
          <FaDownload />
        }
      </Button>
    )
  }));

  return (
    <Offcanvas className="w-50" show={open} onHide={() => onOpenChange?.(false)} placement={placement}>
      <Offcanvas.Header closeButton style={{ borderBottom: '0.5px solid #ddd' }}>
        <Offcanvas.Title>{i18next.t('signaling_detail_title')}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <KeyValueTable items={opportunityData} />

        <h5 className="mt-3">{i18next.t('vehicle')}</h5>
        {vehicleResponse
          ? <div style={{ zoom: 0.8, margin: 1 }}><VehicleCardBody showLinkBtn data={vehicleResponse} showState /></div>
          : <KeyValueTable items={vehicleData} />}

        {!!documentData?.length &&
          <KeyValueTable
            className="mt-3"
            title={i18next.t('documents')}
            items={documentData}
          />}
      </Offcanvas.Body>
    </Offcanvas >
  );
}
